import React, { useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";

interface VisitorSession {
  id: number;
  sessionId: string;
  entryPage: string | null;
  currentPage: string | null;
  referrer: string | null;
  pageCount: number;
  isChatting: boolean;
  conversationId: number | null;
  firstSeen: string;
  lastSeen: string;
  secondsOnSite: number;
  browser: string | null;
  os: string | null;
  country: string | null;
}

interface DayCount { date: string; count: number; }
interface Breakdown { name: string; count: number; pct: number; }

interface VisitorStats {
  onlineNow: number;
  visitsToday: number;
  visitsThisMonth: number;
  last7Days: DayCount[];
  browserBreakdown: Breakdown[];
  osBreakdown: Breakdown[];
  countryBreakdown: Breakdown[];
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatReferrer(referrer: string | null): string {
  if (!referrer) return "Direct";
  try { return new URL(referrer).hostname; }
  catch { return referrer.slice(0, 40); }
}

function formatPage(url: string | null): string {
  if (!url) return "—";
  try {
    const u = new URL(url);
    return u.pathname + (u.search || "");
  } catch { return url.slice(0, 60); }
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={statCardStyle}>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#0f172a", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function MiniBar({ label, pct, count }: { label: string; pct: number; count: number }) {
  return (
    <div style={{ marginBottom: 7 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
        <span style={{ color: "#334155" }}>{label}</span>
        <span style={{ color: "#64748b" }}>{count} ({pct}%)</span>
      </div>
      <div style={{ background: "#e2e8f0", borderRadius: 4, height: 6 }}>
        <div style={{ background: "#3b82f6", borderRadius: 4, height: 6, width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

function Sparkline({ data }: { data: DayCount[] }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  const W = 200, H = 40;
  const step = W / (data.length - 1 || 1);
  const pts = data.map((d, i) => `${i * step},${H - (d.count / max) * H}`).join(" ");
  return (
    <svg width={W} height={H} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke="#3b82f6" strokeWidth={2} strokeLinejoin="round" />
    </svg>
  );
}

export default function VisitorMonitor() {
  const { subscription } = useSubscriptions();
  const api = useApi();
  const [sessions, setSessions] = useState<VisitorSession[]>([]);
  const [stats, setStats] = useState<VisitorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function load() {
    try {
      const [activeRes, statsRes] = await Promise.all([
        api.messaging.get("Visitor/active"),
        api.messaging.get("Visitor/stats"),
      ]);
      const activeData = await activeRes.json();
      const statsData = await statsRes.json();
      setSessions(Array.isArray(activeData?.sessions) ? activeData.sessions : []);
      if (statsData?.enabled !== false) setStats(statsData);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!subscription?.enableVisitorTracking) { setLoading(false); return; }
    load();
    intervalRef.current = setInterval(load, 30_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [subscription?.enableVisitorTracking]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!subscription?.enableVisitorTracking) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h2>Visitor Monitor</h2>
        <p style={{ color: "#666", marginTop: 12 }}>
          Visitor tracking is not enabled on your plan. Contact support to upgrade.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Visitor Monitor</h2>
        <span style={{
          background: sessions.length > 0 ? "#22c55e" : "#94a3b8",
          color: "#fff", borderRadius: 12, padding: "2px 10px", fontSize: 13, fontWeight: 600
        }}>
          {sessions.length} active
        </span>
        <span style={{ marginLeft: "auto", color: "#94a3b8", fontSize: 12 }}>Refreshes every 30s</span>
      </div>

      {/* Stats row */}
      {stats && (
        <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
          <StatCard label="Online Now" value={stats.onlineNow} />
          <StatCard label="Visits Today" value={stats.visitsToday.toLocaleString()} />
          <StatCard label="This Month" value={stats.visitsThisMonth.toLocaleString()} />
          <div style={{ ...statCardStyle, flex: "1 1 220px", minWidth: 220 }}>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Last 7 Days</div>
            <Sparkline data={stats.last7Days} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
              <span>{stats.last7Days[0]?.date?.slice(5)}</span>
              <span>{stats.last7Days[6]?.date?.slice(5)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Breakdowns */}
      {stats && (stats.browserBreakdown.length > 0 || stats.osBreakdown.length > 0 || stats.countryBreakdown.length > 0) && (
        <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
          {stats.browserBreakdown.length > 0 && (
            <div style={breakdownCardStyle}>
              <div style={breakdownTitleStyle}>Browser</div>
              {stats.browserBreakdown.map(b => <MiniBar key={b.name} label={b.name} pct={b.pct} count={b.count} />)}
            </div>
          )}
          {stats.osBreakdown.length > 0 && (
            <div style={breakdownCardStyle}>
              <div style={breakdownTitleStyle}>Operating System</div>
              {stats.osBreakdown.map(b => <MiniBar key={b.name} label={b.name} pct={b.pct} count={b.count} />)}
            </div>
          )}
          {stats.countryBreakdown.length > 0 && (
            <div style={breakdownCardStyle}>
              <div style={breakdownTitleStyle}>Country</div>
              {stats.countryBreakdown.map(b => <MiniBar key={b.name} label={b.name} pct={b.pct} count={b.count} />)}
            </div>
          )}
        </div>
      )}

      {/* Live session table */}
      {loading ? (
        <p style={{ color: "#94a3b8" }}>Loading…</p>
      ) : sessions.length === 0 ? (
        <p style={{ color: "#94a3b8" }}>No active visitors right now.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e2e8f0", textAlign: "left" }}>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Current Page</th>
                <th style={thStyle}>Entry Page</th>
                <th style={thStyle}>From</th>
                <th style={thStyle}>Browser</th>
                <th style={thStyle}>OS</th>
                <th style={thStyle}>Country</th>
                <th style={thStyle}>Pages</th>
                <th style={thStyle}>Time on Site</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={tdStyle}>
                    {s.isChatting
                      ? <span style={{ color: "#2563eb", fontWeight: 600 }}>● Chatting</span>
                      : <span style={{ color: "#22c55e" }}>● Browsing</span>}
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.currentPage ?? ""}>
                    {formatPage(s.currentPage)}
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#64748b" }} title={s.entryPage ?? ""}>
                    {formatPage(s.entryPage)}
                  </td>
                  <td style={{ ...tdStyle, color: "#64748b" }}>{formatReferrer(s.referrer)}</td>
                  <td style={{ ...tdStyle, color: "#475569" }}>{s.browser ?? "—"}</td>
                  <td style={{ ...tdStyle, color: "#475569" }}>{s.os ?? "—"}</td>
                  <td style={{ ...tdStyle, color: "#475569" }}>{s.country ?? "—"}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{s.pageCount}</td>
                  <td style={tdStyle}>{formatDuration(s.secondsOnSite)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const statCardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "16px 20px",
  flex: "0 0 auto",
  minWidth: 120
};

const breakdownCardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "16px 20px",
  flex: "1 1 200px",
  minWidth: 200
};

const breakdownTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 12
};

const thStyle: React.CSSProperties = {
  padding: "8px 12px",
  fontWeight: 600,
  color: "#475569",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.05em"
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  verticalAlign: "middle"
};
